/**
 * Custom Search Header Bar - TOMS Style
 * Handles search overlay dialog, predictive search, and category links
 */

class CustomSearchOverlay {
  /**
   * @param {HTMLDialogElement} dialog - The search dialog element
   */
  constructor(dialog) {
    this.dialog = dialog;
    /** @type {HTMLFormElement} */
    this.form = /** @type {HTMLFormElement} */ (dialog.querySelector('[data-custom-search-form]'));
    /** @type {HTMLInputElement} */
    this.input = /** @type {HTMLInputElement} */ (dialog.querySelector('[data-search-input]'));
    /** @type {NodeListOf<HTMLElement>} */
    this.closeButtons = /** @type {NodeListOf<HTMLElement>} */ (dialog.querySelectorAll('[data-search-close]'));
    /** @type {HTMLElement | null} */
    this.categoriesContainer = dialog.querySelector('[data-search-categories]');
    /** @type {HTMLElement | null} */
    this.predictiveContainer = dialog.querySelector('[data-search-predictive]');
    /** @type {HTMLElement | null} */
    this.resultsContainer = dialog.querySelector('[data-search-results]');

    /** @type {number | null} */
    this.debounceTimer = null;
    /** @type {string} */
    this.currentQuery = '';
    /** @type {boolean} */
    this.isOpen = false;
    /** @type {boolean} */
    this.resultsDisabled = dialog.dataset.searchDisabled === 'true';

    this.init();
  }

  init() {
    // Input events
    this.input.addEventListener('input', this.handleInput.bind(this));
    this.input.addEventListener('focus', this.handleFocus.bind(this));
    
    // Close buttons
    this.closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        this.handleClear(e);
        this.close();
      });
    });

    // Form submit
    this.form.addEventListener('submit', this.handleSubmit.bind(this));

    // Keyboard events
    this.dialog.addEventListener('keydown', this.handleKeydown.bind(this));

    // Watch for header sticky state changes
    this.observeHeaderState();

    // Click outside to close (backdrop)
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) {
        this.close();
      }
    });
  }

  open() {
    if (this.isOpen) return;
    
    this.dialog.showModal();
    this.isOpen = true;
    
    // Focus input after a brief delay
    setTimeout(() => {
      this.input.focus();
    }, 100);

    // Disable body scroll - dialog will scroll instead
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.isOpen) return;
    
    this.dialog.close();
    this.isOpen = false;

    // Re-enable body scroll
    document.body.style.overflow = '';

    // Clear search if desired
    // this.handleClear();
  }

  /**
   * Watch for header sticky state changes and update dialog class
   */
  observeHeaderState() {
    const header = document.querySelector('header #header-component');
    if (!header) return;

    // Create a MutationObserver to watch for attribute changes
    const observer = new MutationObserver(() => {
      const isSticky = header.getAttribute('data-sticky-state') === 'active';
      this.dialog.classList.toggle('header-sticky', isSticky);
    });

    // Start observing
    observer.observe(header, {
      attributes: true,
      attributeFilter: ['data-sticky-state']
    });

    // Set initial state
    const isSticky = header.getAttribute('data-sticky-state') === 'active';
    this.dialog.classList.toggle('header-sticky', isSticky);
  }

  /**
   * @param {Event} e
   */
  handleInput(e) {
    if (this.resultsDisabled) return;

    const query = /** @type {HTMLInputElement} */ (e.target).value.trim();

    // Toggle between categories and predictive search
    if (query.length === 0) {
      this.showCategories();
    } else {
      this.hideCategories();
      this.debouncedSearch(query);
    }
  }

  handleFocus() {
    // Show categories if input is empty
    if (this.input.value.trim().length === 0) {
      this.showCategories();
    }
  }

  /**
   * @param {Event} [e]
   */
  handleClear(e) {
    if (e) e.preventDefault();
    
    this.input.value = '';
    this.currentQuery = '';
    
    // Clear results
    if (this.predictiveContainer) {
      this.predictiveContainer.innerHTML = '';
      this.predictiveContainer.hidden = true;
    }
    
    // Reset View All button counts
    const buttons = document.querySelectorAll('[data-view-all-button]');
    buttons.forEach(button => {
      const countSpan = /** @type {HTMLElement | null} */ (button.querySelector('[data-view-all-count]'));
      if (countSpan) {
        countSpan.classList.remove('is-visible');
        countSpan.textContent = '';
        countSpan.hidden = true;
      }
    });
    
    // Show categories
    this.showCategories();
    
    // Focus input
    this.input.focus();
  }

  /**
   * @param {Event} e
   */
  handleSubmit(e) {
    const query = this.input.value.trim();
    
    // If empty, prevent submit
    if (query.length === 0) {
      e.preventDefault();
      return;
    }

    // Let form submit naturally to search page
  }

  /**
   * @param {KeyboardEvent} e
   */
  handleKeydown(e) {
    // Close on Escape
    if (e.key === 'Escape') {
      this.close();
    }
  }

  showCategories() {
    if (this.categoriesContainer) {
      this.categoriesContainer.hidden = false;
    }
    if (this.predictiveContainer) {
      this.predictiveContainer.hidden = true;
    }
  }

  hideCategories() {
    if (this.categoriesContainer) {
      this.categoriesContainer.hidden = true;
    }
    if (this.predictiveContainer) {
      this.predictiveContainer.hidden = false;
    }
  }

  /**
   * @param {string} query
   */
  debouncedSearch(query) {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  /**
   * @param {string} query
   */
  async performSearch(query) {
    if (query === this.currentQuery) return;
    
    this.currentQuery = query;

    try {
      // Use Shopify's Predictive Search API with Section Rendering
      // See: https://shopify.dev/docs/api/ajax/reference/predictive-search
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,query',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
        section_id: 'custom-predictive-search'
      });

      const response = await fetch(
        `${window.Shopify.routes.root}search/suggest?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Search request failed: ${response.status}`);
      }

      // The response is HTML when using section_id without .json
      const html = await response.text();
      
      if (html && html.trim().length > 0) {
        this.renderResults(html);
        // Fetch total count after rendering results
        this.fetchTotalCount(query);
      } else {
        console.error('Empty HTML response');
        this.renderError();
      }
    } catch (error) {
      console.error('Search error:', error);
      this.renderError();
    }
  }

  /**
   * Fetch total product count for the search query
   * @param {string} query
   */
  async fetchTotalCount(query) {
    try {
      const params = new URLSearchParams({
        q: query,
        type: 'product'
      });

      const response = await fetch(
        `${window.Shopify.routes.root}search?${params.toString()}`
      );

      if (!response.ok) return;

      const html = await response.text();
      
      // Parse HTML to find result count from data-search-count attribute
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Look for the data-search-count attribute we added to the results-list element
      const resultsElement = doc.querySelector('[data-search-count]');
      
      let totalCount = 0;
      if (resultsElement) {
        const countAttr = resultsElement.getAttribute('data-search-count');
        if (countAttr) {
          totalCount = parseInt(countAttr, 10);
        }
      }

      // Update all View All buttons with the count
      if (totalCount > 0) {
        const buttons = document.querySelectorAll('[data-view-all-button]');
        buttons.forEach(button => {
          const countSpan = /** @type {HTMLElement | null} */ (button.querySelector('[data-view-all-count]'));
          if (countSpan) {
            countSpan.innerHTML = `&nbsp;(${totalCount})`;
            countSpan.hidden = false;
            
            // Trigger smooth fade-in animation
            requestAnimationFrame(() => {
              countSpan.classList.add('is-visible');
            });
          }
        });
      }
    } catch (error) {
      console.error('Error fetching total count:', error);
    }
  }

  /**
   * Render the section HTML
   * @param {string} html
   */
  renderResults(html) {
    if (!this.predictiveContainer) return;
    this.predictiveContainer.innerHTML = html;
    this.predictiveContainer.hidden = false;
  }

  renderError() {
    if (!this.predictiveContainer) return;
    
    this.predictiveContainer.innerHTML = `
      <div class="px-5 lg:px-8 py-8 text-center">
        <p>Sorry, we encountered an error. Please try again.</p>
      </div>
    `;
    this.predictiveContainer.hidden = false;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const searchDialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('[data-search-overlay]'));
  
  if (searchDialog) {
    const searchOverlay = new CustomSearchOverlay(searchDialog);
    
    // Make it globally accessible so header button can open it
    window.customSearchOverlay = searchOverlay;
    
    // Listen for custom event to open search
    document.addEventListener('custom-search:open', () => {
      searchOverlay.open();
    });
  }
});

// Export for use in header
export { CustomSearchOverlay };
